/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/

import React, { useEffect, useRef } from "react";
import { useAnimations, useGLTF } from "@react-three/drei";
import {
  useDrawer,
  useOutline,
  useSection,
  useSound,
} from "../../../../common/hooks";
import {
  blackMaterial,
  cameraColor,
  greenTennisBall,
  orangeMaterial,
  darkBlueMaterial,
  whiteBreakMaterial,
  whiteMaterial,
  cameraBlack,
} from "../../../../common/materials";
import { useState } from "react";
import { playBackward, playForward } from "../../../../utils/animations";
// import { useDragConstraint } from "../../../../utils/drag";
import { useBox, useSphere } from "@react-three/cannon";
import { CannonBox } from "../../physics/CannonBox";
import { SECTIONS } from "../../../../constants/sections";
import HobbyContent from "../../../elements/Drawer/contents/HobbyContent";
import { changeIntensity } from "../../../../animations/lights";

const model = "/models/hobbies/stand.glb";
const PhysicsStand = ({ position }) => {
  const size = 0.75;
  return <CannonBox size={[size, size, size]} position={position} />;
};

const PhotoCamera = (props) => {
  const { currentSection } = useSection();
  const { openDrawer } = useDrawer();
  const lightRef = useRef();

  const { nodes, materials } = useGLTF(model);
  const position = [2.755, 1.66, 2.54];
  const rotation = [0, Math.PI / 4, 0];
  const [ref, api] = useBox(
    () => ({
      args: [0.25, 0.185, 0.15],
      mass: 1,
      position,
      rotation,
      type: "Dynamic",
    }),
    useRef(null)
  );

  const { element: soundElement, ref: soundRef } = useSound(
    "/sound/effects/camera_shutter.mp3"
  );

  useEffect(() => {
    if (soundRef) soundRef.setRefDistance(1);
  }, [soundRef]);

  const handleClickCamera = () => {
    const key = "photography";
    if (currentSection.name !== SECTIONS.HOBBIES) return;

    soundRef.play();
    lightRef.current.intensity = 10;
    changeIntensity(lightRef.current, 0, { duration: 100 });

    api.applyLocalImpulse([0, 0.4, 0], [0.1, 0, 0]);
    openDrawer({
      content: <HobbyContent hobby={key} />,
    });
  };

  return (
    <group name="camera" onClick={handleClickCamera} ref={ref} {...props}>
      {soundElement}
      <mesh
        name="camera_1"
        geometry={nodes.camera_1.geometry}
        material={cameraBlack}
      ></mesh>
      <mesh
        name="camera_2"
        geometry={nodes.camera_2.geometry}
        material={cameraColor}
      ></mesh>
      <mesh
        name="camera_3"
        geometry={nodes.camera_3.geometry}
        material={materials.lens}
      />
      <mesh
        name="camera_4"
        geometry={nodes.camera_4.geometry}
        material={materials["camera black.001"]}
      >
        <spotLight
          ref={lightRef}
          castShadow
          intensity={0}
          angle={3}
          distance={5}
          decay={4}
          position={[0.1, 0.05, -0.11]}
        />
      </mesh>
    </group>
  );
};

const Basketball = (props) => {
  const { currentSection } = useSection();
  const { openDrawer } = useDrawer();
  const { nodes } = useGLTF(model);
  const position = [2.53, 0.24, 2.6];
  const [ref, api] = useSphere(
    () => ({
      args: [0.2],
      mass: 5,
      position,
      material: {
        friction: 0.8,
        restitution: 0.1,
      },
      type: "Dynamic",
    }),
    useRef(null)
  );

  const { element: soundElement, ref: soundRef } = useSound(
    "/sound/effects/basketball_bounce.wav"
  );

  const handleClick = () => {
    if (currentSection.name === SECTIONS.HOBBIES) {
      //TODO: this sound should be played when the ball hits the ground
      soundRef.play();

      api.applyImpulse([0, 15, 0], [0, 0, 0]);
      openDrawer({
        content: <HobbyContent hobby="basketball" />,
      });
    }
  };

  return (
    <group {...props} ref={ref} onClick={handleClick}>
      {soundElement}
      <mesh
        castShadow
        receiveShadow
        name="basketball"
        geometry={nodes.basketball_1.geometry}
        material={orangeMaterial}
      ></mesh>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.basketball_2.geometry}
        material={blackMaterial}
      />
    </group>
  );
};

const TennisBall = (props) => {
  const { nodes, materials, animations } = useGLTF(model);

  // const [ballRef] = useSphere(
  //   () => ({
  //     args: [0.1],
  //     mass: 0.1,
  //     position: props.position,
  //     material: {
  //       friction: 0.1,
  //       restitution: 1,
  //     },
  //     collisionFilterGroup: 1,
  //     type: "Dynamic",
  //   }),
  //   useRef(null)
  // );
  // const bind = useDragConstraint(ballRef);

  return (
    // <group {...props} ref={ballRef} {...bind}>
    <group {...props}>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.Sphere3.geometry}
        material={greenTennisBall}
      ></mesh>
      <mesh
        receiveShadow
        geometry={nodes.Sphere3_1.geometry}
        material={materials.grip}
      />
    </group>
  );
};

export default function Stand({ ...props }) {
  const group = useRef();
  const { nodes, animations } = useGLTF(model);
  const { actions } = useAnimations(animations, group);
  const [playedAnimations, setPlayedAnimations] = useState([]);
  const { currentSection } = useSection();
  const { addOutline, clearOutline } = useOutline();
  const { openDrawer } = useDrawer();

  const onPointerEnter = ({ object }) => {
    if (currentSection.name === SECTIONS.HOBBIES) addOutline(object);
  };

  const onPointerLeave = () => {
    if (currentSection.name === SECTIONS.HOBBIES) clearOutline();
  };

  const playAnimation = ({ actionKey, isForward }) => {
    if (isForward) {
      playForward(actions[actionKey]);
      setPlayedAnimations((prev) => [...prev, actionKey]);
    } else {
      playBackward(actions[actionKey]);
      setPlayedAnimations((prev) =>
        prev.filter((animation) => animation !== actionKey)
      );
    }
  };

  const handleClickPadel = () => {
    const key = "padel";
    if (currentSection.name === SECTIONS.HOBBIES) {
      playAnimation({ actionKey: key, isForward: true });
      openDrawer({
        content: <HobbyContent hobby={key} />,
        handleClose: () => playAnimation({ actionKey: key, isForward: false }),
      });
    }
  };

  useEffect(() => {
    if (currentSection.name !== SECTIONS.HOBBIES) {
      playedAnimations.forEach((animation) => {
        playBackward(actions[animation]);
      });
    }
  }, [currentSection]);

  return (
    <group position={[0.008, 0, 0.01]} ref={group} {...props} dispose={null}>
      <Basketball
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      />
      <PhotoCamera
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      />
      <group
        name="padelraquet"
        position={[2.41, 1.03, 2.56]}
        rotation={[2.95, -0.71, -0.72]}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onClick={handleClickPadel}
      >
        <mesh
          castShadow
          receiveShadow
          name="Cube009"
          geometry={nodes.Cube009.geometry}
          material={darkBlueMaterial}
        ></mesh>
        <mesh
          castShadow
          receiveShadow
          name="Cube009_1"
          geometry={nodes.Cube009_1.geometry}
          material={whiteMaterial}
        ></mesh>
        <group
          name="padel_armature"
          position={[-0.04, -0.45, -0.01]}
          scale={0.6}
        >
          <primitive object={nodes.Bone_1} />
        </group>
      </group>
      <TennisBall position={[2.7, 0.84, 2.47]} rotation={[Math.PI / 2, 0, 0]} />
      <TennisBall position={[2.58, 0.84, 2.46]} rotation={[2.26, 0.21, 0.91]} />
      <TennisBall position={[2.64, 0.84, 2.58]} rotation={[0.9, -0.54, -1]} />

      <mesh
        castShadow
        receiveShadow
        name="stand 1"
        geometry={nodes.stand1.geometry}
        material={whiteBreakMaterial}
        position={[2.52, 0.36, 2.7]}
      />
      <mesh
        castShadow
        receiveShadow
        name="stand 2"
        geometry={nodes.stand2.geometry}
        material={whiteBreakMaterial}
        position={[2.52, 1.15, 2.7]}
      />
      <mesh
        castShadow
        receiveShadow
        name="stand 3"
        geometry={nodes.stand3.geometry}
        material={whiteBreakMaterial}
        position={[2.52, 1.94, 2.7]}
      />

      <PhysicsStand position={[2.54, 0.02, 2.65]} />
      <PhysicsStand position={[2.54, 1.56, 2.65]} />
      {/* <PhysicsStand position={[2.5, 2.5, 2.7]} /> */}
    </group>
  );
}

useGLTF.preload(model);
